import React, { useState, useEffect } from "react";
import { Download, FolderOpen, AlertTriangle, Search, Loader2 } from "lucide-react";
import Modal from "./Modal";
import { AxiosResponse } from "axios";
import { getBulkUploadFolders, downloadBulkUserFiles } from "../utils/Api";

interface BulkDownloadModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const BulkDownloadModal: React.FC<BulkDownloadModalProps> = ({ isOpen, setIsOpen }) => {
  const [folders, setFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setError(null);
    setDownloading(null);
    setLoading(true);
    getBulkUploadFolders()
      .then((res: AxiosResponse<{ folders: string[] }>) => setFolders(res.data.folders ?? []))
      .catch(() => setError("Failed to load folders. Please try again."))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleDownload = async (email: string) => {
    setDownloading(email);
    setError(null);
    try {
      const res = await downloadBulkUserFiles(email);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = email.replace("@", "_at_").replace(/\./g, "_");
      a.download = `bulk_uploads_${safe}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError(`Failed to download files for ${email}.`);
    } finally {
      setDownloading(null);
    }
  };

  const filtered = folders.filter((f) =>
    f.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <h1 className="text-center text-2xl font-bold text-gray-900 mb-1">
        Bulk Uploaded Files
      </h1>
      <p className="text-center text-gray-500 text-sm mb-5">
        Select a user to download all their uploaded files as a ZIP archive.
      </p>

      {loading ? (
        <div className="flex flex-col items-center gap-3 py-10">
          <Loader2 size={32} className="text-gray-400 animate-spin" />
          <p className="text-sm text-gray-500">Loading folders…</p>
        </div>
      ) : (
        <>
          {folders.length > 0 && (
            <div className="relative mb-4">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-sky-400 transition-colors"
              />
            </div>
          )}

          {filtered.length === 0 && !error ? (
            <div className="flex flex-col items-center gap-2 py-10 text-gray-400">
              <FolderOpen size={40} />
              <p className="text-sm">
                {folders.length === 0
                  ? "No files have been uploaded yet."
                  : "No results match your search."}
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {filtered.map((email) => (
                <div
                  key={email}
                  className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FolderOpen size={16} className="text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-800 truncate">{email}</span>
                  </div>
                  <button
                    onClick={() => handleDownload(email)}
                    disabled={downloading === email}
                    className="flex items-center gap-1.5 text-xs font-medium text-sky-600 hover:text-sky-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 transition-colors"
                  >
                    {downloading === email ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Download size={14} />
                    )}
                    {downloading === email ? "Downloading…" : "Download"}
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="mt-4 bg-red-50 text-red-600 text-sm p-3 rounded-lg border border-red-200 flex items-start gap-2">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default BulkDownloadModal;
